import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import { apiRequest } from '../../utils';
import './MemberHome.css';
import '../storefront/Storefront.css';

/* Bravos MEMBERSHIP checkout (/join) — the twin of MemberCheckout (/book/:eventId), pointed at
   the SEASON instead of a Lab. That distinction is the whole point: seats bought on the Season
   issue EVENT-LESS, so they form a pool the member spends on whichever Labs they attend, and the
   unspent ones roll into the next season. Two tabs — for me, or for my company.

   The order still goes through the shared /api/storefront/checkout, so voucher validation, Stripe,
   ticket issuance and membership creation are all the paths that already run in production. */

const Field = ({ label, children }) => (
  <div className="auth__field">
    <label className="auth__label">{label}</label>
    <div className="auth__input-wrap">{children}</div>
  </div>
);

const money = (currency, value) => `${currency} ${Number(value).toFixed(2)}`;

const MembershipCheckout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // 'individual' | 'company'. The member home hands the company tab in via ROUTER STATE, not a
  // query param — usePersistUrlParams strips query params on arrival.
  const [tab, setTab] = useState(location.state?.tab === 'company' ? 'company' : 'individual');
  const [rateId, setRateId] = useState(null);
  const [voucher, setVoucher] = useState('');
  const [voucherApplied, setVoucherApplied] = useState(null);
  const [voucherError, setVoucherError] = useState(null);
  const [voucherLoading, setVoucherLoading] = useState(false);
  const [terms, setTerms] = useState(false);
  const [company, setCompany] = useState({ name: '', vat: '' });
  const [addr, setAddr] = useState({ street: '', number: '', box: '', zip: '', city: '', country: '' });

  // One key per mount: a double submit must never buy two memberships (and 20 tickets).
  const [idempotencyKey] = useState(() =>
    (window.crypto && window.crypto.randomUUID)
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

  useEffect(() => {
    (async () => {
      try {
        const d = await apiRequest('GET', '/bravos/membership/catalog');
        setCatalog(d);
        const p = d.prefill || {};
        setAddr({
          street: p.street || '', number: p.number || '', box: p.box || '',
          zip: p.zip || '', city: p.city || '', country: p.country || '',
        });
        if (d.my_company) setCompany({ name: d.my_company.name || '', vat: d.my_company.vat || '' });
        const startTab = location.state?.tab === 'company' && d.company?.length ? 'company' : 'individual';
        setTab(startTab);
        const first = (startTab === 'company' ? d.company : d.individual) || [];
        if (first.length) setRateId(first[0].rate_id);
        // A voucher the member already holds applies to the individual membership — pre-fill and
        // show it as applied, exactly as the Lab checkout does.
        if (d.held_voucher && d.held_voucher.code && startTab === 'individual') {
          setVoucher(d.held_voucher.code);
          setRateId(d.held_voucher.rate_id || (first.length ? first[0].rate_id : null));
          setVoucherApplied({ code: d.held_voucher.code, discount: d.held_voucher.discount });
        }
      } catch (err) {
        setError(t(err.message, { defaultValue: err.message }));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const products = useMemo(
    () => (catalog ? (tab === 'company' ? catalog.company : catalog.individual) || [] : []),
    [catalog, tab],
  );
  const product = useMemo(
    () => products.find((p) => p.rate_id === rateId) || null,
    [products, rateId],
  );

  const setAddrField = (k) => (e) => setAddr((a) => ({ ...a, [k]: e.target.value }));
  const clearVoucherPreview = () => { setVoucherApplied(null); setVoucherError(null); };
  const chooseRate = (id) => { setRateId(id); clearVoucherPreview(); };

  const chooseTab = (next) => {
    setTab(next);
    clearVoucherPreview();
    const list = (next === 'company' ? catalog?.company : catalog?.individual) || [];
    setRateId(list.length ? list[0].rate_id : null);
  };

  const applyVoucher = async () => {
    const code = voucher.trim();
    if (!code || !product || !catalog?.season) return;
    setVoucherLoading(true);
    setVoucherError(null);
    try {
      // The Lab voucher-preview endpoint takes any Bravos event id, the Season included.
      const d = await apiRequest('POST', `/bravos/lab/${catalog.season.id}/voucher-preview`,
        { code, rate_id: product.rate_id });
      if (d && d.ok && d.discount > 0) {
        setVoucherApplied({ code: d.code, discount: d.discount });
      } else {
        setVoucherApplied(null);
        // The endpoint answers with the translation key for the real reason (api/voucher_errors.py);
        // `voucher_limit` makes "already used" say how often the code may be used.
        setVoucherError(t((d && d.error) || 'voucherInvalid',
                          { count: d?.voucher_limit, defaultValue: t('voucherInvalid') }));
      }
    } catch (err) {
      setVoucherApplied(null);
      setVoucherError(t(err.message, { count: err.data?.voucher_limit, defaultValue: t('voucherInvalid') }));
    } finally {
      setVoucherLoading(false);
    }
  };

  const basePrice = product ? (product.effective_price ?? product.unit_price) : 0;
  const discount = voucherApplied ? Math.min(voucherApplied.discount, basePrice) : 0;
  const netPrice = Math.max(0, basePrice - discount);
  const vatAmount = catalog && catalog.with_vat && product
    ? netPrice * (Number(product.vat_rate) || 0) / 100 : 0;
  const total = netPrice + vatAmount;

  const seats = (product?.tickets || [])
    .filter((x) => x.kind === 'seat')
    .reduce((n, x) => n + (x.quantity || 0), 0);

  const join = async (e) => {
    e.preventDefault();
    if (!product) return;
    if (!addr.street || !addr.zip || !addr.city || !addr.country) {
      setError(t('fillRequiredFields'));
      return;
    }
    if (tab === 'company' && (!company.name.trim() || !company.vat.trim())) {
      setError(t('fillRequiredFields'));
      return;
    }
    if (catalog.terms_url && !terms) {
      setError(t('fillRequiredFields'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const p = catalog.prefill || {};
      const data = await apiRequest('POST', '/storefront/checkout', {
        event_id: catalog.season.id,
        mode: 'full',
        basket: [{ rate_id: product.rate_id, qty: 1 }],
        idempotency_key: idempotencyKey,
        slug: catalog.slug,
        first_name: p.first_name, last_name: p.last_name, email: p.email, mobile: p.mobile,
        ...addr,
        ...(tab === 'company'
          ? { company_order: true, company_name: company.name.trim(), company_vat: company.vat.trim() }
          : {}),
        voucher_code: voucher.trim(),
        terms_accepted: terms,
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      navigate('/member-home');
    } catch (err) {
      setError(t(err.message, { defaultValue: err.message }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Base activeNav="home">
      <Card>
        <Back to="/member-home" />
        <Title my="1" className="mt-3">
          <Icon name="star" theme="primary" className="me-2" />
          {t('joinTitle')}
        </Title>

        {loading ? (
          <span className="auth__spinner" />
        ) : !catalog || !products.length ? (
          <p className="text-muted mb-0">
            {error || t('shopUnavailable', { defaultValue: t('storefrontNotFound') })}
          </p>
        ) : (
          <form onSubmit={join}>
            {catalog.company && catalog.company.length > 0 && (
              <div className="bravos-jointabs">
                <button type="button"
                        className={`bravos-jointab${tab === 'individual' ? ' bravos-jointab--on' : ''}`}
                        onClick={() => chooseTab('individual')}>
                  {t('joinForMe')}
                </button>
                <button type="button"
                        className={`bravos-jointab${tab === 'company' ? ' bravos-jointab--on' : ''}`}
                        onClick={() => chooseTab('company')}>
                  {t('joinForCompany')}
                </button>
              </div>
            )}

            {seats > 0 && (
              <p className="bravos-q__hint">{t('joinLead', { n: seats })} {t('ticketsNeverExpire')}</p>
            )}

            {products.map((p) => (
              <label key={p.rate_id} className="storefront__summary"
                     style={{ display: 'block', cursor: 'pointer' }}>
                <div className="storefront__product-head">
                  <span className="storefront__product-name">
                    {products.length > 1 && (
                      <input type="radio" name="rate" checked={rateId === p.rate_id}
                             onChange={() => chooseRate(p.rate_id)} className="me-2" />
                    )}
                    {p.name}
                  </span>
                  <span className="storefront__product-price">
                    {money(catalog.currency, p.effective_price ?? p.unit_price)}
                  </span>
                </div>
                {(p.tickets || []).length > 0 && (
                  <div className="bravos-q__hint" style={{ marginTop: 4 }}>
                    {p.tickets.map((x) => `${x.quantity} × ${x.name}`).join(' · ')}
                  </div>
                )}
              </label>
            ))}

            {product && (discount > 0 || catalog.with_vat) && (
              <div className="storefront__summary">
                {discount > 0 && (
                  <div className="storefront__product-head">
                    <span>{t('voucherApplied')} ({voucherApplied.code})</span>
                    <span>− {money(catalog.currency, discount)}</span>
                  </div>
                )}
                {catalog.with_vat && (
                  <div className="storefront__product-head">
                    <span>{t('vat')} ({Number(product.vat_rate)}%)</span>
                    <span>{money(catalog.currency, vatAmount)}</span>
                  </div>
                )}
                <div className="storefront__product-head storefront__total">
                  <span>{t('orderTotal')}</span>
                  <span>{money(catalog.currency, total)}</span>
                </div>
              </div>
            )}

            {tab === 'company' && (
              <>
                {/* An existing company is pre-filled and billed as-is: the checkout attaches a
                    repeat B2B order to the company this buyer already orders for rather than
                    minting a second one, so a typed-over name would be silently discarded. */}
                {catalog.my_company && (
                  <p className="bravos-q__hint">{t('joinBillsCompany', { name: catalog.my_company.name })}</p>
                )}
                <Field label={t('companyName')}>
                  <input className="auth__input" type="text" value={company.name}
                         onChange={(e) => setCompany((c) => ({ ...c, name: e.target.value }))}
                         readOnly={!!catalog.my_company} required />
                </Field>
                <Field label={t('vatNumber')}>
                  <input className="auth__input" type="text" value={company.vat}
                         onChange={(e) => setCompany((c) => ({ ...c, vat: e.target.value }))}
                         required />
                </Field>
              </>
            )}

            <Field label={t('voucherCode')}>
              <div className="d-flex" style={{ gap: '0.5rem' }}>
                <input className="auth__input" type="text" value={voucher}
                       onChange={(e) => { setVoucher(e.target.value); clearVoucherPreview(); }}
                       placeholder={t('voucherCodePlaceholder')} />
                <button type="button" className="bravos-submit bravos-submit--ghost"
                        style={{ marginTop: 0, width: 'auto', flexShrink: 0, padding: '0 18px' }}
                        onClick={applyVoucher}
                        disabled={voucherLoading || !voucher.trim() || !product}>
                  {voucherLoading ? <span className="auth__spinner" /> : t('applyVoucher')}
                </button>
              </div>
            </Field>
            {discount > 0 && (
              <p className="bravos-q__hint" style={{ color: '#1a7f4b' }}>
                {t('voucherApplied')} · −{money(catalog.currency, discount)}
              </p>
            )}
            {voucherError && <p className="text-danger mb-2">{voucherError}</p>}

            <div className="auth__row">
              <Field label={t('street')}>
                <input className="auth__input" type="text" value={addr.street}
                       onChange={setAddrField('street')} autoComplete="address-line1" required />
              </Field>
              <Field label={t('number')}>
                <input className="auth__input" type="text" value={addr.number}
                       onChange={setAddrField('number')} />
              </Field>
            </div>
            <div className="auth__row">
              <Field label={t('zip')}>
                <input className="auth__input" type="text" value={addr.zip}
                       onChange={setAddrField('zip')} autoComplete="postal-code" required />
              </Field>
              <Field label={t('city')}>
                <input className="auth__input" type="text" value={addr.city}
                       onChange={setAddrField('city')} autoComplete="address-level2" required />
              </Field>
            </div>
            <Field label={t('country')}>
              <input className="auth__input" type="text" value={addr.country}
                     onChange={setAddrField('country')} autoComplete="country-name" required />
            </Field>

            {catalog.terms_url && (
              <label className="auth__hint d-flex align-items-start" style={{ gap: '0.5rem' }}>
                <input type="checkbox" checked={terms}
                       onChange={(e) => setTerms(e.target.checked)} required />
                <span>
                  {t('iAcceptThe')}{' '}
                  <a href={catalog.terms_url} target="_blank" rel="noreferrer">
                    {t('termsAndConditions')}
                  </a>
                </span>
              </label>
            )}

            {error && <p className="text-danger">{error}</p>}

            <button type="submit" className="bravos-submit" disabled={submitting || !product}>
              {submitting ? <span className="auth__spinner" /> : <Icon name="credit-card" />}
              {t('payAndJoin')}
            </button>
          </form>
        )}
      </Card>
    </Base>
  );
};

export default MembershipCheckout;
